import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, type toneForStatus } from '@ibe/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { useSoftDeleteGateway } from '../api/mutations.js';
import { useGateway } from '../api/queries.js';
import { type GatewayDetail } from '../api/schemas.js';

function statusTone(active: boolean): ReturnType<typeof toneForStatus> {
  return active ? 'success' : 'neutral';
}

function defaultLabel(gateway: GatewayDetail): string {
  return gateway.isDefault ? 'Default for this scope' : 'Not the default';
}

export function NotificationGatewayDetailPage(): React.JSX.Element {
  const gatewayId = useParams().gatewayId ?? '';
  const navigate = useNavigate();
  const query = useGateway(gatewayId);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const softDelete = useSoftDeleteGateway();

  const onDelete = async (): Promise<void> => {
    await softDelete.mutateAsync(gatewayId);
    setConfirmingDelete(false);
    void navigate('/notifications');
  };

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.NOTIFICATION_READ}
      parent={{ to: '/notifications', label: 'Notification gateways' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.NOTIFICATION_CONFIGURE}>
            <Button asChild variant="outline">
              <Link to={`/notifications/${gatewayId}/edit`}>Edit</Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.NOTIFICATION_CONFIGURE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data) => (
        <>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete gateway"
            description={`${data.name} will be removed. Its audit history and any dispatches that used it are retained.`}
            confirmLabel="Delete"
            destructive
            loading={softDelete.isPending}
            onConfirm={() => {
              void onDelete();
            }}
          />

          <DetailGrid>
            <DetailField label="Channel">{data.channel}</DetailField>
            <DetailField label="Provider">{data.provider}</DetailField>
            <DetailField label="Status">
              <StatusBadge tone={statusTone(data.isActive)}>
                {data.isActive ? 'Active' : 'Inactive'}
              </StatusBadge>
            </DetailField>
            <DetailField label="Default">{defaultLabel(data)}</DetailField>
            <DetailField label="Priority">{String(data.priority)}</DetailField>
            <DetailField label="From address">{data.fromAddress}</DetailField>
            <DetailField label="From name">{data.fromName}</DetailField>
            <DetailField label="Credentials">
              {data.credentialsConfigured ? 'Configured' : 'Not configured'}
            </DetailField>
            <DetailField label="Tenant">{data.tenantId}</DetailField>
          </DetailGrid>
        </>
      )}
    </ResourceDetailPage>
  );
}
